import { createContext, type ReactNode, useContext } from "react";
import type {
  GroupConfig,
  VariableDefinition,
} from "@babackapp/variables-registry";
import type { BlockCategory } from "@babackapp/editor-blocks-library/blocks";
import type { EmailTemplateDomain } from "@babackapp/private-api-client/client/use-get-all-shop-email-templates";

/**
 * Per-domain configuration for the templates editor core.
 *
 * The core (EditorCore, the variables picker, the templates context) is
 * generic; each dedicated wrapper supplies the domain, the variable set and the
 * variable group labels through this context:
 *  - TemplatesEditor (tracking) → tracking variables + groups, domain "tracking"
 *  - ReturnTemplatesEditor (return) → return variables + groups, domain "return"
 */
export type TemplatesEditorConfig = {
  domain: EmailTemplateDomain;
  /** The variable list exposed in the picker and used for preview values. */
  variables: VariableDefinition[];
  /** Group label/icon config keyed by variable group id. */
  groups: Record<string, GroupConfig>;
  /**
   * Block categories to hide from the block picker for this domain.
   * E.g. the return editor hides `BlockCategory.TRACKING` so tracking-only
   * blocks stay exclusive to the tracking editor.
   */
  hiddenBlockCategories?: BlockCategory[];
  /**
   * Whether to show the "linked workflows / not linked" banner above the
   * template name. Defaults to shown; the return editor sets it to false since
   * returns have no workflow concept yet.
   */
  showWorkflowDependencies?: boolean;
  /**
   * Whether to show the "edit emails theme" button. Defaults to shown; the
   * return editor hides it (the theme is managed from the tracking editor).
   */
  showThemeButton?: boolean;
  /** Max-width of the email canvas in the editor (px). Defaults to 480. */
  contentMaxWidth?: number;
};

const TemplatesEditorConfigContext = createContext<
  TemplatesEditorConfig | undefined
>(undefined);

export function TemplatesEditorConfigProvider({
  config,
  children,
}: {
  config: TemplatesEditorConfig;
  children: ReactNode;
}) {
  return (
    <TemplatesEditorConfigContext.Provider value={config}>
      {children}
    </TemplatesEditorConfigContext.Provider>
  );
}

export function useTemplatesEditorConfig(): TemplatesEditorConfig {
  const context = useContext(TemplatesEditorConfigContext);

  if (context === undefined) {
    throw new Error(
      "useTemplatesEditorConfig must be used within a TemplatesEditorConfigProvider",
    );
  }
  return context;
}
